import {
  GaussianSplatItem,
  isGaussianSplatLoading,
  isGaussianSplatVisible,
  useCapabilityRegistryContext,
  useDataContext,
} from '@mobile-mapping/providers';
import { FitToViewModelCapabilityCoordinator } from '@trimble/web3d';
import { t } from '@trimble/i18n';
import {
  SelectionTree,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import { createMemo, JSX } from 'solid-js';
import { SharedGaussianSplatsTreeContainerProps } from './shared-panel.types';

export const SharedGaussianSplatsTreeContainer = (
  props: SharedGaussianSplatsTreeContainerProps
): JSX.Element => {
  const { dataState, dataLoadingService } = useDataContext();
  const { capabilityRegistry } = useCapabilityRegistryContext();

  const fitToViewCoordinator = capabilityRegistry.get(
    FitToViewModelCapabilityCoordinator
  );

  const selectionTreeNodes = createMemo(
    (): TreeNodeDefinition<GaussianSplatItem>[] =>
      (props.nodes ?? []).map((node) => {
        const item = node.data;
        if (item === undefined) {
          return node;
        }

        return {
          ...node,
          contextMenuOptions: [
            {
              text: t('siteVisualization.sidePanelLayout.fitView'),
              onClick: () => {
                void fitToViewCoordinator()?.fitToView(item.id);
              },
              disabled: () =>
                !isGaussianSplatVisible(dataState, item.id) ||
                isGaussianSplatLoading(dataState, item.id),
            },
            {
              text: t('siteVisualization.sidePanelLayout.showOnly'),
              onClick: () => {
                dataLoadingService.loadExclusive('gaussianSplat', [item.id]);
              },
            },
          ],
        } satisfies TreeNodeDefinition<GaussianSplatItem>;
      })
  );

  return (
    <div data-testid="gaussian-splat-selection-tree">
      <SelectionTree
        nodes={selectionTreeNodes()}
        childrenLoadStatus={props.childrenLoadStatus ?? 'success'}
        isNodeLoading={(nodeId) => isGaussianSplatLoading(dataState, nodeId)}
        isNodeVisible={(nodeId) => isGaussianSplatVisible(dataState, nodeId)}
        onVisibilityToggle={(nodeId) => {
          if (
            isGaussianSplatLoading(dataState, nodeId) ||
            isGaussianSplatVisible(dataState, nodeId)
          ) {
            dataLoadingService.unload('gaussianSplat', nodeId);
          } else {
            dataLoadingService.load('gaussianSplat', nodeId);
          }
        }}
        onCancelVisibilityToggle={(nodeId) => {
          if (isGaussianSplatLoading(dataState, nodeId)) {
            dataLoadingService.unload('gaussianSplat', nodeId);
          }
        }}
        multiSelect={false}
      />
    </div>
  );
};
